import React, { useMemo } from 'react';
import { CardHcdAvailabilityOutputDTO } from '@maiia/model/generated/model/api-patient/api-patient';

import { Button, cn, Typography } from '@kiiwi/ui';
import { dayjsWithTimezone } from '@/src/utils';
import { DEFAULT_TIMEZONE } from '@/src/constants';
import ProCardFirstAvailability from '@/components/organisms/ProCard/ProCardFirstAvailability';
import ProCardDescription from '@/components/organisms/ProCard/ProCardDescription';
import useResponsive from '@/src/hooks/useResponsive';

import { useTranslation } from '@/src/i18n';
import ProCardSubstituteInfo from '@/components/organisms/ProCard/ProCardSubstituteInfo';
import ProCardAvailabilitiesTimeSlot from '@/components/organisms/ProCard/ProCardAvailabilitiesTimeSlot';

type ProCardAvailabilitiesCalendarProps = {
  card: CardHcdAvailabilityOutputDTO;
  goToHcdPage: (event: React.MouseEvent, slot?: string) => void;
  onClickTimeSlot?: (event: React.MouseEvent) => void;
  onClickCTA?: (event: React.MouseEvent) => void;
};

const ProCardAvailabilitiesCalendar = ({
  card,
  goToHcdPage,
  onClickTimeSlot: onClickTimeSlotFromProps,
  onClickCTA: onClickCTAFromProps,
}: ProCardAvailabilitiesCalendarProps) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const {
    practitioner: { id: practitionerId } = {},
    center: { id: centerId, timeZone = DEFAULT_TIMEZONE } = {},
    nextAvailability,
    availabilities = [],
  } = card;

  const dayjsTZ = dayjsWithTimezone(timeZone);

  const boundedAvailabilities = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, index) => {
        const date = dayjsTZ()
          .add(index, 'day')
          .format('YYYY-MM-DD');
        const availability = availabilities.find(c => c.date === date);

        return { ...availability, date };
      }),
    [availabilities],
  );

  const substitute = useMemo(
    () => boundedAvailabilities?.find(c => c?.hasSubstitute)?.substitute,
    [boundedAvailabilities],
  );

  const noAvailibility = useMemo(
    () =>
      !boundedAvailabilities.find(
        c => c.firstAvailability || c.lastAvailability,
      ),
    [boundedAvailabilities],
  );

  const onClickCTA = (event: React.MouseEvent) => {
    onClickCTAFromProps?.(event);
    goToHcdPage(event);
  };

  const onClickTimeSlot = (event: React.MouseEvent, slot?: string) => {
    onClickTimeSlotFromProps?.(event);
    goToHcdPage(event, slot);
  };

  if (noAvailibility && !nextAvailability)
    return (
      <ProCardDescription>
        {t('pro_card_appointments_unavailable_for_now')}
      </ProCardDescription>
    );

  if (isMobile && noAvailibility && nextAvailability)
    return (
      <ProCardFirstAvailability
        date={dayjsTZ(nextAvailability)}
        className="w-full"
        onClick={goToHcdPage}
      />
    );

  return (
    <div className="percy-hidden">
      <div className="relative h-max text-sm">
        <div
          className={cn(
            'grid grid-flow-col grid-cols-4 gap-3',
            isMobile
              ? 'grid-rows-[auto_32px_32px]'
              : 'grid-rows-[auto_32px_32px_auto]',
          )}
        >
          <div className="row-span-4 grid grid-rows-subgrid">
            <div className="row-start-2 flex items-center">
              {t('pro_card_appointments_available_from')}
            </div>
            <div className="row-start-3 flex items-center">
              {t('pro_card_appointments_available_to')}
            </div>
          </div>

          <div className="col-span-3 grid grid-flow-row grid-cols-subgrid">
            {boundedAvailabilities.map(({ date }) => (
              <div
                key={`${date}_header`}
                className="flex flex-col items-center justify-center"
              >
                <Typography
                  className="font-semibold capitalize"
                  variant="body-2"
                >
                  {`${dayjsTZ(date).format('ddd')}`}
                </Typography>
                <Typography variant="body-2" className="text-neutral-secondary">
                  {`${dayjsTZ(date).format('DD/MM')}`}
                </Typography>
              </div>
            ))}
          </div>

          <div className="relative col-span-3 row-span-2 grid grid-flow-col grid-cols-subgrid grid-rows-subgrid">
            {boundedAvailabilities.map(
              ({
                date,
                firstAvailability,
                lastAvailability,
                substitute: slotSubstitute,
              }) => (
                <div
                  key={`${dayjsTZ(date).format(
                    'DD-MM-YYYY',
                  )}_${centerId}_${practitionerId}`}
                  className="row-span-2 grid grid-rows-subgrid"
                >
                  {[firstAvailability, lastAvailability].map((slot, index) => (
                    <ProCardAvailabilitiesTimeSlot
                      key={`${dayjsTZ(date).format(
                        'DD-MM-YYYY',
                      )}_${centerId}_${practitionerId}_${
                        slot ? dayjsTZ(slot).format('HH:mm') : index
                      }`}
                      slot={slot ? dayjsTZ(slot) : undefined}
                      hasSubstitute={!!slotSubstitute}
                      onTimeSlotClick={e => onClickTimeSlot(e, slot)}
                    />
                  ))}
                </div>
              ),
            )}

            {noAvailibility && nextAvailability && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <ProCardFirstAvailability
                  date={dayjsTZ(nextAvailability)}
                  onClick={goToHcdPage}
                />
              </div>
            )}
          </div>

          {!isMobile && (
            <div className="col-span-3 col-start-2 row-start-4 flex flex-col gap-3">
              <Button className="w-full" size="sm" onClick={onClickCTA}>
                {t('get-appointment')}
              </Button>

              {substitute && <ProCardSubstituteInfo substitute={substitute} />}
            </div>
          )}
        </div>
      </div>

      {isMobile && substitute && (
        <ProCardSubstituteInfo substitute={substitute} />
      )}
    </div>
  );
};

export default ProCardAvailabilitiesCalendar;
